import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";

import type { ListEventCategories } from "../../../api";
import type { AppModel } from "../../../app/app-model";

import { AddButton } from "../../../components/ui-kit/add-button";
import { useShortcut } from "../../../utils/use-shortcut";
import {
  AddEventsDialog,
  hideShortcut,
  useEventsDraft,
} from "./add-events-dialog";

/**
 * The "Add events" button and the dialog it opens. Owns the draft so that hiding
 * the dialog keeps the typed rows, and so adding, removing or expanding a row
 * re-renders only this subtree instead of the events table behind it.
 */
export function AddEventsAction({
  app,
  eventOptions,
  onSaved,
}: {
  app: AppModel;
  eventOptions: ListEventCategories | null;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const draft = useEventsDraft();
  // Counted when the dialog hides — nothing can change it until it re-opens, so
  // the draft's rows stay out of this component's render.
  const [hiddenRowCount, setHiddenRowCount] = useState(0);

  function openDialog() {
    setHiddenRowCount(0);
    setOpen(true);
  }

  /** The count is passed in, so it's counted once per gesture rather than twice. */
  function hideDialog(startedRowCount: number) {
    setHiddenRowCount(startedRowCount);
    setOpen(false);
  }

  function closeDialog() {
    setOpen(false);
    draft.reset();
  }

  // Toggling the dialog, so peeking at the events already on record and coming
  // back never needs the mouse. Deliberately fires while typing — hiding
  // mid-sentence is the point — which is also why it captures: the rich-text
  // editors bind Mod-E to inline code, and consuming the event first keeps them
  // from acting on it too.
  useShortcut(
    hideShortcut.key,
    () => (open ? hideDialog(draft.startedRowCount()) : openDialog()),
    {
      capture: true,
      // Only act when our own dialog is the only one on screen. Captured
      // page-wide, an unscoped binding would steal Mod-E from the row-edit
      // dialog's editors, or hide this dialog out from under the discard prompt.
      // Closing dialogs linger for their exit animation, so don't count those.
      enabled: () =>
        document.querySelectorAll(
          "[data-slot='dialog-content']:not([data-state='closed'])",
        ).length === (open ? 1 : 0),
    },
  );

  return (
    <>
      <AddButton
        onClick={openDialog}
        badge={hiddenRowCount}
        aria-keyshortcuts={hideShortcut.aria}
        title={
          hiddenRowCount > 0
            ? `${plural(hiddenRowCount, {
                one: "Continue adding events — # unsaved row kept",
                other: "Continue adding events — # unsaved rows kept",
              })} (${hideShortcut.hint})`
            : `${t`Add events`} (${hideShortcut.hint})`
        }
      >
        <Trans>Add events</Trans>
      </AddButton>
      {open && (
        <AddEventsDialog
          app={app}
          eventOptions={eventOptions}
          draft={draft}
          onHide={hideDialog}
          onClose={closeDialog}
          onSaved={onSaved}
        />
      )}
    </>
  );
}
